import { absoluteUrl, request } from '@/api/http/httpClient';
import { sessionToken } from '@/api/http/session';
import type { BaseApi } from './baseApi';
import type { Bootstrap, Organization, SignInResult, Welcome } from './types';

/** Keeps the session secret the server just issued, so every later call carries it. */
function begin(result: SignInResult): SignInResult {
  sessionToken.set(result.token);
  return result;
}

/** The service answers a logo as a path on itself; a picture on the page needs the whole address. */
const withLogoUrls = (organization: Organization): Organization => ({
  ...organization,
  logoUrl: absoluteUrl(organization.logoUrl),
  squareLogoUrl: absoluteUrl(organization.squareLogoUrl),
  coverPhotoUrl: absoluteUrl(organization.coverPhotoUrl),
});

/** A query string from the values that are present. */
function query(values: Record<string, string | number | boolean | string[] | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) continue;
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Base over HTTP: the same surface as the prototype (`BaseApi`), talking to the Django backend. */
export const baseHttpApi: BaseApi = {
  welcome: async () => {
    const welcome = await request<Welcome>('GET', '/platform/welcome', { authenticated: false });
    return welcome.organization ? { ...welcome, organization: { ...welcome.organization, logoUrl: absoluteUrl(welcome.organization.logoUrl), coverPhotoUrl: absoluteUrl(welcome.organization.coverPhotoUrl) } } : welcome;
  },
  bootstrap: async () => {
    const bootstrap = await request<Bootstrap>('GET', '/platform/bootstrap');
    return { ...bootstrap, organization: withLogoUrls(bootstrap.organization) };
  },

  auth: {
    signIn: async (email, password) => begin(await request<SignInResult>('POST', '/auth/sign-in', { body: { email, password }, authenticated: false })),
    async signOut() {
      try {
        await request('POST', '/auth/sign-out');
      } finally {
        sessionToken.clear(); // even if the service could not be reached, this window no longer holds a session
      }
    },
    forgotPassword: async (email) => {
      await request('POST', '/auth/forgot-password', { body: { email }, authenticated: false });
    },
    resetPassword: (input) => request('POST', '/auth/reset-password', { body: input, authenticated: false }),
    acceptInvitation: (input) => request('POST', '/auth/accept-invitation', { body: input, authenticated: false }),
    changePassword: (input) => request('POST', '/auth/change-password', { body: input }),
    updateProfile: (input) => request('PATCH', '/auth/me', { body: input }),
  },

  setup: {
    run: async (input) => begin(await request<SignInResult>('POST', '/platform/setup', { body: input, authenticated: false })),
  },

  users: {
    list: () => request('GET', '/users'),
    invite: (input) => request('POST', '/users', { body: input }),
    update: (id, input) => request('PATCH', `/users/${id}`, { body: input }),
    setActive: (id, active) => request('POST', `/users/${id}/${active ? 'activate' : 'deactivate'}`),
    issueCode: (id) => request('POST', `/users/${id}/code`),
  },

  roles: {
    list: () => request('GET', '/roles'),
    create: (input) => request('POST', '/roles', { body: input }),
    update: (key, patch) => request('PATCH', `/roles/${key}`, { body: patch }),
    delete: (key) => request('DELETE', `/roles/${key}`),
  },

  organization: {
    update: async (patch) => withLogoUrls(await request<Organization>('PATCH', '/organization', { body: patch })),
    uploadLogo: async (variant, file) => {
      const form = new FormData();
      form.append('file', file, 'logo');
      return withLogoUrls(await request<Organization>('POST', `/organization/logo?variant=${variant}`, { form }));
    },
    removeLogo: async (variant) => withLogoUrls(await request<Organization>('DELETE', `/organization/logo?variant=${variant}`)),
  },

  apps: {
    list: () => request('GET', '/apps'),
    install: (id) => request('POST', `/apps/${id}/install`),
    uninstall: (id) => request('POST', `/apps/${id}/uninstall`),
  },

  sports: {
    list: () => request('GET', '/sports'),
    get: (id) => request('GET', `/sports/${id}`),
    create: (input) => request('POST', '/sports', { body: input }),
    update: (id, patch) => request('PATCH', `/sports/${id}`, { body: patch }),
    remove: (id) => request('DELETE', `/sports/${id}`),
  },

  sportLists: {
    list: (kind, q = {}) => request('GET', `/sport-lists/${kind}${query({ sportId: q.sportId, effective: q.effective, active: q.active })}`),
    create: (kind, input) => request('POST', `/sport-lists/${kind}`, { body: input }),
    update: (kind, id, patch) => request('PATCH', `/sport-lists/${kind}/${id}`, { body: patch }),
    remove: (kind, id) => request('DELETE', `/sport-lists/${kind}/${id}`),
    reorder: (kind, ids, sportId) => request('POST', `/sport-lists/${kind}/order`, { body: { ids, sportId: sportId ?? null } }),
  },

  seasons: {
    list: (q = {}) => request('GET', `/seasons${query({ sportId: q.sportId, status: q.status })}`),
    get: (id) => request('GET', `/seasons/${id}`),
    create: (input) => request('POST', '/seasons', { body: input }),
    update: (id, patch) => request('PATCH', `/seasons/${id}`, { body: patch }),
    remove: (id) => request('DELETE', `/seasons/${id}`),
  },

  locations: {
    list: () => request('GET', '/locations'),
    create: (input) => request('POST', '/locations', { body: input }),
    update: (id, patch) => request('PATCH', `/locations/${id}`, { body: patch }),
  },

  reportSettings: {
    get: () => request('GET', '/report-settings'),
    update: (patch) => request('PATCH', '/report-settings', { body: patch }),
  },

  reportFiles: {
    pdf: (slug, document) => request('POST', `/reports/${slug}/pdf`, { body: document, as: 'blob' }),
  },

  audit: {
    list: (params = {}) => request('GET', `/audit-events${query({ limit: params.limit, action: params.action, before: params.before })}`),
  },

  search: (q) => request('GET', `/search${query({ q })}`),

  timeline: (params) => request('GET', `/timeline${query({ athleteId: params.athleteId, injuryId: params.injuryId })}`),

  backups: {
    status: () => request('GET', '/backups/status'),
    history: () => request('GET', '/backups'),
    create: () => request('POST', '/backups'),
    updateSettings: (patch) => request('PATCH', '/backups/settings', { body: patch }),
    inspectPackage: (path) => request('POST', '/backups/inspect', { body: { path } }),
    restore: (path, confirm) => request('POST', '/backups/restore', { body: { path, confirm } }),
  },

  system: {
    checks: () => request('GET', '/system/checks'),
  },
};
